import DOMPurify from 'dompurify'

/**
 * Converts an HTML string to GitHub-Flavored Markdown.
 *
 * Client-safe: uses browser-native DOMParser + DOMPurify. Do NOT import this
 * from server-only code (no `import 'server-only'` here by design).
 *
 * Supported elements: b/strong, em/i, h1–h3, ul/ol/li (nested), a, code,
 * blockquote, hr, p, div, br. Images are dropped.
 */
export function htmlToMarkdown(html: string): string {
  if (!html || !html.trim()) return ''

  // Sanitize first to prevent XSS and strip dangerous attributes
  const sanitized = DOMPurify.sanitize(html)

  if (!sanitized.trim()) return ''

  // Parse the sanitized HTML into a DOM tree
  const doc = new DOMParser().parseFromString(sanitized, 'text/html')

  const result = convertNode(doc.body, 0).trim()
  return result
}

// ---------------------------------------------------------------------------
// Core DOM walker
// ---------------------------------------------------------------------------

function convertNode(node: Node, listDepth: number): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeMarkdown(node.textContent ?? '')
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return ''

  const el = node as Element
  const tag = el.tagName.toLowerCase()

  switch (tag) {
    case 'h1':
      return `\n\n# ${convertChildren(el, listDepth).trim()}\n\n`
    case 'h2':
      return `\n\n## ${convertChildren(el, listDepth).trim()}\n\n`
    case 'h3':
      return `\n\n### ${convertChildren(el, listDepth).trim()}\n\n`

    case 'strong':
    case 'b':
      return `**${convertChildren(el, listDepth)}**`

    case 'em':
    case 'i':
      return `*${convertChildren(el, listDepth)}*`

    case 'code': {
      const text = el.textContent ?? ''
      return `\`${text}\``
    }

    case 'a': {
      const href = el.getAttribute('href') ?? ''
      const safeHref = sanitizeHref(href)
      const text = convertChildren(el, listDepth)
      if (!safeHref) return text
      // Encode unmatched ')' in the URL so it doesn't break the markdown link syntax.
      const encodedHref = safeHref.replace(/\)/g, '%29')
      return `[${text}](${encodedHref})`
    }

    case 'blockquote': {
      const inner = convertChildren(el, listDepth)
        .trim()
        .split('\n')
        .map(line => `> ${line}`)
        .join('\n')
      return `\n\n${inner}\n\n`
    }

    case 'hr':
      return '\n\n---\n\n'

    case 'br':
      return '\n'

    case 'ul':
      return convertList(el, 'ul', listDepth)

    case 'ol':
      return convertList(el, 'ol', listDepth)

    case 'li':
      // li items are handled inside convertList; reaching here means a
      // bare <li> outside a list — treat as paragraph
      return `\n- ${convertChildren(el, listDepth).trim()}`

    case 'p':
      return `\n\n${convertChildren(el, listDepth).trim()}\n\n`

    case 'div':
      return `\n\n${convertChildren(el, listDepth).trim()}\n\n`

    case 'img':
      // Drop images per spec
      return ''

    case 'script':
    case 'style':
      return ''

    default:
      return convertChildren(el, listDepth)
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function convertChildren(el: Element, listDepth: number): string {
  let out = ''
  for (const child of Array.from(el.childNodes)) {
    out += convertNode(child, listDepth)
  }
  return out
}

function convertList(
  el: Element,
  type: 'ul' | 'ol',
  listDepth: number,
): string {
  const indent = '  '.repeat(listDepth)
  let index = 1
  let out = listDepth === 0 ? '\n\n' : ''

  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType !== Node.ELEMENT_NODE) continue
    const item = child as Element
    if (item.tagName.toLowerCase() !== 'li') continue

    const bullet = type === 'ul' ? '-' : `${index}.`
    index++

    // Separate the direct text/inline content of this <li> from any nested
    // list children so we can render them on the next indented line.
    let inlineContent = ''
    let nestedLists = ''

    for (const liChild of Array.from(item.childNodes)) {
      if (
        liChild.nodeType === Node.ELEMENT_NODE &&
        ['ul', 'ol'].includes((liChild as Element).tagName.toLowerCase())
      ) {
        nestedLists += convertList(
          liChild as Element,
          (liChild as Element).tagName.toLowerCase() as 'ul' | 'ol',
          listDepth + 1,
        )
      } else {
        inlineContent += convertNode(liChild, listDepth + 1)
      }
    }

    const lineText = inlineContent.trim()
    out += `${indent}${bullet} ${lineText}`
    if (nestedLists) {
      out += `\n${nestedLists.trimEnd()}`
    }
    out += '\n'
  }

  if (listDepth === 0) out += '\n'
  return out
}

/**
 * Allowlists safe protocols (http, https, mailto, relative).
 * Rejects anything else (javascript:, data:, vbscript:, etc.).
 */
function sanitizeHref(href: string): string {
  const trimmed = href.trim()
  if (!trimmed) return ''
  const lower = trimmed.toLowerCase()
  if (
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('#') ||
    lower.startsWith('/') ||
    lower.startsWith('./')
  ) {
    return trimmed
  }
  // Reject all other protocols (javascript:, data:, vbscript:, etc.)
  if (/^[a-z][a-z0-9+\-.]*:/i.test(trimmed)) return ''
  // Relative path without leading slash or ./
  return trimmed
}

/** Escapes markdown-significant characters in plain text nodes. */
function escapeMarkdown(text: string): string {
  // Escape *, _, [, ], and \ which can create unintended markdown formatting.
  return text.replace(/([\\*_[\]])/g, '\\$1')
}
